/**
 * Welcome Modal
 * =============
 * Shows once on first login after signup or invite acceptance.
 *
 * Two variants:
 * 1. New tenant owner (admin) — Use-case selection + API key created notice
 * 2. Invited user (non-admin) — Role-aware welcome with capabilities
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Code2,
  ShieldCheck,
  Search,
  Key,
  X,
  ArrowRight,
  Eye,
  FileText,
  Users,
  BarChart3,
  Settings,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';

// Role-based capabilities for invited users
const ROLE_CAPABILITIES = {
  admin: {
    label: 'Administrator',
    items: [
      { icon: Settings, text: 'Full access to all features and settings' },
      { icon: Users, text: 'Manage team members and invitations' },
      { icon: ShieldCheck, text: 'Configure verification workflows' },
      { icon: BarChart3, text: 'View analytics and audit logs' },
    ],
  },
  reviewer: {
    label: 'Reviewer',
    items: [
      { icon: Eye, text: 'Review and approve applicant verifications' },
      { icon: FileText, text: 'Access documents and biometric data' },
      { icon: ShieldCheck, text: 'Review screening results' },
      { icon: BarChart3, text: 'View compliance reports' },
    ],
  },
  analyst: {
    label: 'Analyst',
    items: [
      { icon: Eye, text: 'View applicants and verification results' },
      { icon: FileText, text: 'Review documents and biometric data' },
      { icon: ShieldCheck, text: 'Access screening results' },
    ],
  },
  viewer: {
    label: 'Viewer',
    items: [
      { icon: Eye, text: 'View applicants and verification results' },
      { icon: FileText, text: 'Read-only access to documents' },
      { icon: ShieldCheck, text: 'View screening results' },
    ],
  },
};

// Use-case options for new tenant owners
const USE_CASES = [
  {
    id: 'developer',
    icon: Code2,
    title: 'Developer',
    description: 'Integrate verification into my app',
  },
  {
    id: 'compliance',
    icon: ShieldCheck,
    title: 'Compliance',
    description: "Manage my team's KYC operations",
  },
  {
    id: 'exploring',
    icon: Search,
    title: 'Just Exploring',
    description: 'See what it does',
  },
];

export default function WelcomeModal({ onClose, tenantName, userRole }) {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const [selectedUseCase, setSelectedUseCase] = useState(null);

  // Parse first name: Auth0 profile > display name > email prefix (chris.diyanni@ → Chris)
  const emailPrefix = user?.email?.split('@')[0]?.split(/[._-]/)[0] || '';
  const firstName = user?.given_name
    || (user?.name && !user.name.includes('@') ? user.name.split(' ')[0] : '')
    || (emailPrefix ? emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1) : 'there');
  const isOwner = userRole === 'admin';
  const displayTenantName = tenantName || 'your workspace';

  const handleUseCaseSelect = (useCaseId) => {
    setSelectedUseCase(useCaseId);
  };

  const handleContinue = () => {
    if (isOwner && selectedUseCase) {
      localStorage.setItem('gc_user_role', selectedUseCase);
    }
    localStorage.setItem('gc_welcome_seen', 'true');
    onClose(selectedUseCase);
  };

  const handleCopyKey = async () => {
    // Navigate to integrations where they can see/copy the actual key
    navigate('/integrations');
    handleContinue();
  };

  // Invited user variant
  if (!isOwner) {
    const capabilities = ROLE_CAPABILITIES[userRole] || ROLE_CAPABILITIES.viewer;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
        <div
          className={cn(
            'bg-grey-900 border border-grey-800 rounded-2xl w-full max-w-[480px] overflow-hidden',
            'animate-in fade-in-0 zoom-in-95 duration-200'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <div className="flex justify-end p-4 pb-0">
            <button
              onClick={handleContinue}
              className="text-grey-500 hover:text-grey-300 p-1 rounded-lg hover:bg-grey-800 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-10 pb-10 pt-2 text-center">
            <h1 className="text-display-xxs text-grey-50 mb-2">
              Welcome to {displayTenantName}!
            </h1>
            <p className="text-body-sm text-grey-400 mb-6">
              You've been added as {capabilities.label === 'Administrator' ? 'an' : 'a'}{' '}
              <span className="text-teal-400 font-medium">{capabilities.label}</span>.
            </p>

            {/* Capabilities card */}
            <div className="bg-grey-800/50 rounded-xl p-5 text-left mb-8 border border-grey-700/50">
              <p className="text-body-xs text-grey-400 uppercase tracking-wider font-semibold mb-3">
                What you can do
              </p>
              <div className="space-y-3">
                {capabilities.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <item.icon size={16} className="text-teal-400 shrink-0" />
                    <span className="text-body-sm text-grey-200">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              onClick={handleContinue}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white gap-2"
            >
              Go to Dashboard
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // New tenant owner variant
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div
        className={cn(
          'bg-grey-900 border border-grey-800 rounded-2xl w-full max-w-[560px] overflow-hidden',
          'animate-in fade-in-0 zoom-in-95 duration-200'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={handleContinue}
            className="text-grey-500 hover:text-grey-300 p-1 rounded-lg hover:bg-grey-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-10 pb-10 pt-2">
          <div className="text-center mb-8">
            <h1 className="text-display-xxs text-grey-50 mb-2">
              Welcome to TrustGate, {firstName}!
            </h1>
            <p className="text-body-sm text-grey-400">
              Your identity verification platform is ready.
            </p>
          </div>

          {/* Use-case question */}
          <p className="text-body-sm text-grey-300 font-medium mb-4">
            What brings you here?
          </p>

          <div className="grid grid-cols-3 gap-3 mb-8">
            {USE_CASES.map((useCase) => {
              const isSelected = selectedUseCase === useCase.id;
              return (
                <div
                  key={useCase.id}
                  onClick={() => handleUseCaseSelect(useCase.id)}
                  className={cn(
                    'p-4 rounded-xl border cursor-pointer transition-all duration-150 text-center',
                    isSelected
                      ? 'border-teal-500 bg-teal-500/10'
                      : 'border-grey-700 bg-grey-800/50 hover:border-grey-600 hover:bg-grey-800'
                  )}
                >
                  <div
                    className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3',
                      isSelected ? 'bg-teal-500/20 text-teal-400' : 'bg-grey-700 text-grey-400'
                    )}
                  >
                    <useCase.icon size={20} />
                  </div>
                  <p
                    className={cn(
                      'text-body-sm font-semibold mb-1',
                      isSelected ? 'text-teal-400' : 'text-grey-200'
                    )}
                  >
                    {useCase.title}
                  </p>
                  <p className="text-body-xs text-grey-500">{useCase.description}</p>
                </div>
              );
            })}
          </div>

          {/* API key notice */}
          <div className="bg-grey-800/50 rounded-xl p-4 mb-6 border border-grey-700/50 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-500/20 flex items-center justify-center shrink-0">
              <Key size={18} className="text-teal-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-body-sm text-grey-200 font-medium">Your API key is ready</p>
              <p className="text-body-xs text-grey-500">Live and test keys auto-created</p>
            </div>
            <button
              onClick={handleCopyKey}
              className="text-body-xs text-teal-400 hover:text-teal-300 font-medium shrink-0 flex items-center gap-1"
            >
              View Keys <ArrowRight size={12} />
            </button>
          </div>

          {/* Plan info */}
          <p className="text-body-xs text-grey-500 text-center mb-6">
            Free Developer plan: 1,000 verifications/month
          </p>

          {/* Continue button */}
          <Button
            onClick={handleContinue}
            disabled={!selectedUseCase}
            className={cn(
              'w-full gap-2',
              selectedUseCase
                ? 'bg-teal-600 hover:bg-teal-500 text-white'
                : 'bg-grey-700 text-grey-500 cursor-not-allowed'
            )}
          >
            {selectedUseCase ? 'Continue' : 'Select an option to continue'}
            {selectedUseCase && <ArrowRight size={16} />}
          </Button>
        </div>
      </div>
    </div>
  );
}
